import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { createFileRoute, Outlet, useNavigate, useLocation } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuthenticatedUser } from 'src/hooks/useAuthenticatedUser';
import { getPartnerCompanyEvent, getEventTypes, getFeatureFlags } from 'src/api/partner';
import { GenericDetailPage } from '@repo/components';
import { EventInfoBox } from '@repo/components';
import EventTabs from 'src/components/partner-portal/EventTabs';
import { EventForm } from '@repo/forms';
import Alert from '@mui/material/Alert';
import { ConfirmDialog, Spinner } from '@repo/components';
import { getVerifyPermission } from '@repo/shared/helpers';
import { PERMISSIONS } from '@repo/shared/domain';
import { EventEmitter } from '@repo/shared/helpers';
import { guardedClient as request } from '@repo/shared/helpers';
import { useBreadcrumbLevel } from '@repo/components/hooks';

export const Route = createFileRoute('/partner_/$partnerId/events/$eventId')({
	component: RouteComponent,
});

function RouteComponent() {
	const { user: authenticatedUser, loading: userLoading } = useAuthenticatedUser();
	const { partnerId, eventId } = Route.useParams();
	const navigate = useNavigate();
	const location = useLocation();
	const queryClient = useQueryClient();
	const verifyPermission = getVerifyPermission(authenticatedUser?.permissions || []);
	const [isFormOpened, setIsFormOpened] = useState(false);
	const [isEventArchiveDialogOpen, setIsEventArchiveDialogOpen] = useState(false);

	// Get event data
	const {
		data: event,
		isLoading: eventLoading,
		error: eventError,
	} = useQuery({
		queryKey: ['partner-company-event', partnerId, eventId],
		queryFn: () => getPartnerCompanyEvent(partnerId, eventId),
		enabled: !userLoading && !!authenticatedUser,
	});

	useBreadcrumbLevel(1, 'Events', `/partner/${partnerId}/events`);
	useBreadcrumbLevel(2, event?.name || 'Event Details');

	// Get event types (for the form)
	const { data: eventTypesResponse, isLoading: eventTypesLoading } = useQuery({
		queryKey: ['event-types'],
		queryFn: () => getEventTypes(),
		enabled: !userLoading && !!authenticatedUser,
	});

	const eventTypes = eventTypesResponse?.data || [];

	// Get feature flags
	const { data: featureFlags } = useQuery({
		queryKey: ['feature-flags'],
		queryFn: () => getFeatureFlags(),
		enabled: !userLoading && !!authenticatedUser,
	});

	const isOwnEvent = useMemo(
		() => !!event?.adminIds?.includes(authenticatedUser?.id || ''),
		[event, authenticatedUser?.id],
	);

	const isOwnGroupEvent = useMemo(() => event?.groupIds?.includes(partnerId), [event, partnerId]);

	const defaultEventData = useMemo(
		() => ({
			...event,
		}),
		[event],
	);

	const handleEditEvent = useCallback(
		async (formData: Record<string, unknown>) => {
			if (formData && event) {
				const dataToSend = { ...formData };
				delete (dataToSend as { type?: unknown }).type;
				try {
					await request.put(`/api/partner-portal/groups/${partnerId}/events/${event.id}`, dataToSend);
					EventEmitter.emit('toast', {
						type: 'success',
						message: 'Event saved',
					});
					queryClient.invalidateQueries({ queryKey: ['partner-company-event', partnerId, eventId] });
					setIsFormOpened(false);
				} catch {
					EventEmitter.emit('toast', {
						type: 'error',
						message: 'Failed to save Event. Did you enter all data correctly?',
					});
				}
			}
		},
		[partnerId, event, eventId, queryClient],
	);

	const handleOpenForm = useCallback(() => {
		setIsFormOpened(true);
	}, []);

	const handleCloseForm = useCallback(() => {
		setIsFormOpened(false);
	}, []);

	const handleOpenEventArchiveDialog = useCallback(() => {
		setIsEventArchiveDialogOpen(true);
	}, []);

	const handleCloseEventArchiveDialog = useCallback(() => {
		setIsEventArchiveDialogOpen(false);
	}, []);

	const archiveEvent = useCallback(async () => {
		if (!event) return;
		try {
			await request.del(`/api/partner-portal/groups/${partnerId}/events/${event.id}`, {});
			queryClient.invalidateQueries({ queryKey: ['partner-company-events', partnerId] });
			setIsEventArchiveDialogOpen(false);
			navigate({ to: `/partner/${partnerId}/events` });
		} catch {
			EventEmitter.emit('toast', {
				type: 'error',
				message: 'Failed to archive event.',
			});
		}
	}, [partnerId, event, queryClient, navigate]);

	// Calculate which tabs should be available (same logic as EventTabs)
	const availableTabs = useMemo(() => {
		if (!event) return [];
		const tabs: Array<{ id: string }> = [];
		// Details: only show if event has a Form and form !== 'event' and is own group event
		if (event?.type?.Form && event.type.form !== 'event' && isOwnGroupEvent) {
			tabs.push({ id: 'details' });
		}
		// Users: show if user has permission (like admin panel)
		if (verifyPermission(PERMISSIONS.partnerCompanyUsersView)) {
			tabs.push({ id: 'users' });
		}
		// Subcontracting: only show if event has projectId, feature flag is enabled, and usesSubEvents (same as admin panel)
		if (event?.projectId && featureFlags?.USES_SUBCONTRACTING) {
			tabs.push({ id: 'subcontracting' });
		}
		// Tasks: only show if is own group event
		if (isOwnGroupEvent) {
			tabs.push({ id: 'tasks' });
		}
		// Documents: only show if has permission and is own group event
		if (verifyPermission(PERMISSIONS.partnerCompanyDocumentsView) && isOwnGroupEvent) {
			tabs.push({ id: 'documents' });
		}
		// Logs: only show if is own group event
		if (isOwnGroupEvent) {
			tabs.push({ id: 'logs' });
		}
		return tabs;
	}, [event, verifyPermission, isOwnGroupEvent, featureFlags]);

	// Redirect to first available tab when on the exact event root (no child route)
	useEffect(() => {
		if (!event || availableTabs.length === 0) return;
		const pathSegments = location.pathname.split('/').filter(Boolean);
		const isOnEventRoot = pathSegments[pathSegments.length - 1] === eventId;
		if (isOnEventRoot) {
			// Redirect to the first available tab
			const firstTab = availableTabs[0];
			if (firstTab) {
				navigate({ to: `/partner/${partnerId}/events/${eventId}/${firstTab.id}` });
			}
		}
	}, [location.pathname, eventId, partnerId, navigate, event, availableTabs]);

	if (userLoading || eventLoading || eventTypesLoading) {
		return (
			<div className="flex items-center justify-center h-full">
				<Spinner className="text-[#1E1E1E]" />
			</div>
		);
	}

	if (eventError || !event) {
		// Redirect back to events list if event not found
		navigate({ to: `/partner/${partnerId}/events` });
		return null;
	}

	return (
		<>
			<GenericDetailPage>
				<GenericDetailPage.Content>
					<GenericDetailPage.Content.Notifications>
						{event.status === 'inReview' ? (
							<Alert severity="error" variant="filled" sx={{ '& ul': { margin: 0, paddingLeft: 2 } }}>
								Event needs to be reviewed before allowing access
								<ul>{event?.requirements?.map((r: any) => r.issues?.map((i: any) => <li key={i}>{i}</li>))}</ul>
							</Alert>
						) : null}
						{event.status === 'exception' ? (
							<Alert severity="error" variant="filled">
								Exception procedure active — access is denied until the exception is accepted.
								{event.exception?.reason && (
									<>
										<br />
										Reason: {event.exception.reason}
									</>
								)}
							</Alert>
						) : null}
						{event.exception?.isException && event.status !== 'exception' ? (
							<Alert severity="warning" variant="filled">
								This event was accepted via the exception procedure.
								{event.exception?.reason && (
									<>
										<br />
										Exception reason: {event.exception.reason}
									</>
								)}
								{event.exception?.acceptReason && (
									<>
										<br />
										Accept reason: {event.exception.acceptReason}
									</>
								)}
							</Alert>
						) : null}
					</GenericDetailPage.Content.Notifications>
					<GenericDetailPage.Content.Notifications>
						{event.markedForReview?.length && event.markedForReview.includes('invalidMain') ? (
							<Alert severity="error" variant="filled" sx={{ '& ul': { margin: 0, paddingLeft: 2 } }}>
								Invalid main company
							</Alert>
						) : null}
					</GenericDetailPage.Content.Notifications>
					<EventInfoBox
						event={event}
						openEditForm={handleOpenForm}
						openArchiveDialog={handleOpenEventArchiveDialog}
						permissions={{
							canEdit: verifyPermission(PERMISSIONS.partnerCompanyEventsEdit) && isOwnEvent,
							canArchive: false,
							canDuplicate: verifyPermission(PERMISSIONS.partnerCompanyEventsCreate),
							canReview: false,
						}}
					/>
				</GenericDetailPage.Content>

				<EventTabs
					event={event}
					verifyPermission={verifyPermission}
					isOwnGroupEvent={isOwnGroupEvent}
					featureFlags={featureFlags}
				>
					<Outlet />
				</EventTabs>
			</GenericDetailPage>

			<ConfirmDialog
				isOpen={isEventArchiveDialogOpen}
				cancelAction={handleCloseEventArchiveDialog}
				confirmAction={archiveEvent}
				cancelLabel="Cancel"
				confirmLabel="Archive"
			>
				Are you sure you want to archive this event?
			</ConfirmDialog>

			<EventForm
				isOpen={isFormOpened}
				data={defaultEventData}
				onClose={handleCloseForm}
				onSubmit={handleEditEvent}
				types={eventTypes}
				isCompanyPortalEvent={true}
				projectGetUrl={`/api/partner-portal/groups/${partnerId}/projects/`}
			/>
		</>
	);
}
